import LRUCache from 'lru-cache';
import {create} from 'zustand';

import {type RunSignature} from '../../../types/run';
import {isAbortError} from '../../../util/requests';
import {
  fetchBlobBytes,
  type FetchBlobOptions,
  getObjectURLForBlob,
} from './blob';
import {
  genericBlobFailureMessage,
  isExpiredUrlError,
  tileMediaErrorKindFromBlobFailure,
} from './errors';
import {type TileMediaErrorKind} from './inMemoryCache';
import {type FilePathKey, getKeyForFilePath} from './keys';

/** Evict before typical ~60m GCS signed-URL expiry. */
export const DEFAULT_FILE_METADATA_TTL_MS = 55 * 60 * 1000;

export type PanelFileBlobStatus = 'idle' | 'loading' | 'ready' | 'error';

export type PanelFileBlobSnapshot = {
  status: PanelFileBlobStatus;
  blob: Blob | null;
  objectURL: string | null;
  pending: boolean;
  error: string | undefined;
  errorKind: TileMediaErrorKind | undefined;
};

export const IDLE_PANEL_FILE_BLOB: PanelFileBlobSnapshot = {
  status: 'idle',
  blob: null,
  objectURL: null,
  pending: false,
  error: undefined,
  errorKind: undefined,
};

/** Default LRU capacity for workspace-scoped blob snapshots (count-based). */
export const WORKSPACE_BLOB_CACHE_MAX_ENTRIES = 500;

type PanelFileMetadata = {
  directURL: string;
  metadataCachedAt: number;
  /** Cleared on each syncMetadata write; set when expiry telemetry is taken. */
  metadataExpiryNotified: boolean;
};

export type FetchIfNeededOptions = FetchBlobOptions & {
  /**
   * Called once per entry when the signed URL is rejected as expired and
   * resolves with the refreshed direct URL. The entry returns to idle so the
   * next `fetchIfNeeded` with the fresh URL refetches. If the refresh yields
   * no URL or the same URL, the expiry is stored as an error; a second expiry
   * on the same entry is stored as an error too.
   */
  onUrlExpired?: () => Promise<string | null | undefined>;
};

type WorkspaceBlobStoreState = {
  entries: Partial<Record<FilePathKey, PanelFileBlobSnapshot>>;
  getSnapshot: (filePathKey: FilePathKey) => PanelFileBlobSnapshot;
  fetchIfNeeded: (
    filePathKey: FilePathKey,
    directURL: string | null,
    options?: FetchIfNeededOptions
  ) => void;
  hasMetadata: (filePathKey: FilePathKey) => boolean;
  getMetadataDirectURL: (filePathKey: FilePathKey) => string | undefined;
  isMetadataExpired: (filePathKey: FilePathKey, now?: number) => boolean;
  /** True once per metadata expiry episode (until syncMetadata writes fresh metadata). */
  takeMetadataExpiryEvent: (filePathKey: FilePathKey, now?: number) => boolean;
  syncMetadata: (
    filePathKey: FilePathKey,
    input: SyncPanelFileMetadataInput
  ) => void;
  touch: (filePathKey: FilePathKey) => void;
  invalidate: (filePathKey: FilePathKey) => void;
  resetForTests: () => void;
};

export type SyncPanelFileMetadataInput = {
  directURL: string;
  metadataCachedAt?: number;
};

export type WorkspaceBlobStoreOptions = {
  maxEntries?: number;
  metadataTtlMs?: number;
};

export function getPanelFilePathKey(
  runSignature: RunSignature,
  filePath: string
): FilePathKey {
  return getKeyForFilePath(runSignature, filePath);
}

function buildSnapshot(
  status: PanelFileBlobStatus,
  {
    blob = null,
    objectURL = null,
    error,
    errorKind,
  }: {
    blob?: Blob | null;
    objectURL?: string | null;
    error?: string;
    errorKind?: TileMediaErrorKind;
  } = {}
): PanelFileBlobSnapshot {
  return {
    status,
    blob,
    objectURL,
    pending: status === 'loading',
    error,
    errorKind,
  };
}

export function createWorkspaceBlobStore(
  options: WorkspaceBlobStoreOptions = {}
) {
  const metadataTtlMs = options.metadataTtlMs ?? DEFAULT_FILE_METADATA_TTL_MS;

  return create<WorkspaceBlobStoreState>()((set, get) => {
    const metadataByKey = new Map<FilePathKey, PanelFileMetadata>();
    const inFlightByKey = new Map<FilePathKey, Promise<void>>();
    const directURLByKey = new Map<FilePathKey, string>();
    const directURLUsedByKey = new Map<FilePathKey, string>();
    const urlRefreshedByKey = new Set<FilePathKey>();

    const removeEntry = (filePathKey: FilePathKey) => {
      set(state => {
        if (state.entries[filePathKey] == null) {
          return state;
        }
        const {[filePathKey]: _removed, ...rest} = state.entries;
        return {entries: rest};
      });
      directURLByKey.delete(filePathKey);
      directURLUsedByKey.delete(filePathKey);
      urlRefreshedByKey.delete(filePathKey);
    };

    const lru = new LRUCache<FilePathKey, PanelFileBlobSnapshot>({
      max: options.maxEntries ?? WORKSPACE_BLOB_CACHE_MAX_ENTRIES,
      dispose: (filePathKey, snapshot) => {
        const current = get().entries[filePathKey];
        if (current === snapshot) {
          removeEntry(filePathKey);
        }
      },
    });

    const setEntry = (
      filePathKey: FilePathKey,
      status: PanelFileBlobStatus,
      fields: {
        blob?: Blob | null;
        objectURL?: string | null;
        error?: string;
        errorKind?: TileMediaErrorKind;
      } = {}
    ) => {
      const prev = get().entries[filePathKey];
      const snapshot = buildSnapshot(status, {
        blob: fields.blob ?? prev?.blob ?? null,
        objectURL: fields.objectURL ?? prev?.objectURL ?? null,
        error: 'error' in fields ? fields.error : prev?.error,
        errorKind: 'errorKind' in fields ? fields.errorKind : prev?.errorKind,
      });

      set(state => ({
        entries: {
          ...state.entries,
          [filePathKey]: snapshot,
        },
      }));

      if (status === 'ready') {
        lru.set(filePathKey, snapshot);
      }
    };

    const shouldEvictEntry = (
      filePathKey: FilePathKey,
      directURL: string
    ): boolean => {
      const current = get().entries[filePathKey];
      if (current?.status !== 'ready') {
        return false;
      }
      const metadata = metadataByKey.get(filePathKey);
      const directURLUsed = directURLUsedByKey.get(filePathKey);
      if (directURLUsed != null && directURLUsed !== directURL) {
        return true;
      }
      if (metadata != null && metadata.directURL !== directURL) {
        return true;
      }
      return false;
    };

    const clearBlobEntry = (filePathKey: FilePathKey) => {
      lru.del(filePathKey);
      inFlightByKey.delete(filePathKey);
      removeEntry(filePathKey);
    };

    const setFailure = (filePathKey: FilePathKey, err: unknown) => {
      setEntry(filePathKey, 'error', {
        error: genericBlobFailureMessage(err),
        errorKind: tileMediaErrorKindFromBlobFailure(err),
      });
    };

    // A refreshed URL is picked up by the caller's next fetchIfNeeded; only
    // a refresh that yields nothing new settles the entry here.
    const refreshExpiredURL = async (
      filePathKey: FilePathKey,
      expiredURL: string,
      err: unknown,
      onUrlExpired: () => Promise<string | null | undefined>
    ) => {
      let freshURL: string | null | undefined;
      try {
        freshURL = await onUrlExpired();
      } catch {
        freshURL = null;
      }
      if (!directURLByKey.has(filePathKey)) {
        return;
      }
      if (
        (freshURL == null || freshURL === expiredURL) &&
        get().entries[filePathKey]?.status === 'idle'
      ) {
        setFailure(filePathKey, err);
      }
    };

    const startFetch = (
      filePathKey: FilePathKey,
      directURL: string,
      options?: FetchIfNeededOptions
    ) => {
      if (inFlightByKey.has(filePathKey)) {
        return;
      }

      const current = get().entries[filePathKey];
      if (current?.status === 'ready' || current?.status === 'error') {
        return;
      }

      directURLByKey.set(filePathKey, directURL);
      directURLUsedByKey.set(filePathKey, directURL);
      setEntry(filePathKey, 'loading');

      const onUrlExpired = options?.onUrlExpired;
      const fetchOptions =
        options == null ? undefined : {logFailure: options.logFailure};
      const fetchPromise = fetchBlobBytes(directURL, fetchOptions)
        .then(blob => {
          if (!directURLByKey.has(filePathKey)) {
            return;
          }
          urlRefreshedByKey.delete(filePathKey);
          setEntry(filePathKey, 'ready', {
            blob,
            objectURL: getObjectURLForBlob(blob),
            error: undefined,
            errorKind: undefined,
          });
        })
        .catch(err => {
          if (!directURLByKey.has(filePathKey)) {
            return;
          }
          // Aborted (page unloading) is not an error: return to idle so
          // fetchIfNeeded refetches on the next read instead of showing an
          // error state.
          if (isAbortError(err)) {
            setEntry(filePathKey, 'idle', {
              error: undefined,
              errorKind: undefined,
            });
            return;
          }
          if (
            isExpiredUrlError(err) &&
            onUrlExpired != null &&
            !urlRefreshedByKey.has(filePathKey)
          ) {
            urlRefreshedByKey.add(filePathKey);
            setEntry(filePathKey, 'idle', {
              error: undefined,
              errorKind: undefined,
            });
            void refreshExpiredURL(filePathKey, directURL, err, onUrlExpired);
            return;
          }
          setFailure(filePathKey, err);
        })
        .finally(() => {
          inFlightByKey.delete(filePathKey);
        });

      inFlightByKey.set(filePathKey, fetchPromise);
    };

    const getSnapshot = (filePathKey: FilePathKey): PanelFileBlobSnapshot => {
      if (lru.has(filePathKey)) {
        lru.get(filePathKey);
      }
      return get().entries[filePathKey] ?? IDLE_PANEL_FILE_BLOB;
    };

    return {
      entries: {},

      getSnapshot,

      hasMetadata(filePathKey) {
        return metadataByKey.has(filePathKey);
      },

      isMetadataExpired(filePathKey, now = Date.now()) {
        const metadata = metadataByKey.get(filePathKey);
        if (metadata == null) {
          return false;
        }
        return now >= metadata.metadataCachedAt + metadataTtlMs;
      },

      takeMetadataExpiryEvent(filePathKey, now = Date.now()) {
        const metadata = metadataByKey.get(filePathKey);
        if (metadata == null) {
          return false;
        }
        if (now < metadata.metadataCachedAt + metadataTtlMs) {
          return false;
        }
        if (metadata.metadataExpiryNotified) {
          return false;
        }
        metadata.metadataExpiryNotified = true;
        return true;
      },

      getMetadataDirectURL(filePathKey) {
        return metadataByKey.get(filePathKey)?.directURL;
      },

      syncMetadata(filePathKey, input) {
        const existing = metadataByKey.get(filePathKey);
        if (existing != null && existing.directURL === input.directURL) {
          return;
        }
        const metadataCachedAt = input.metadataCachedAt ?? Date.now();
        metadataByKey.set(filePathKey, {
          directURL: input.directURL,
          metadataCachedAt,
          metadataExpiryNotified: false,
        });
      },

      fetchIfNeeded(filePathKey, directURL, options) {
        if (directURL == null) {
          return;
        }

        if (shouldEvictEntry(filePathKey, directURL)) {
          clearBlobEntry(filePathKey);
        }

        const current = getSnapshot(filePathKey);
        if (
          current.status === 'ready' ||
          current.status === 'loading' ||
          current.status === 'error'
        ) {
          return;
        }

        startFetch(filePathKey, directURL, options);
      },

      touch(filePathKey) {
        if (lru.has(filePathKey)) {
          lru.get(filePathKey);
        }
      },

      invalidate(filePathKey) {
        if (
          get().entries[filePathKey] == null &&
          !metadataByKey.has(filePathKey)
        ) {
          return;
        }
        clearBlobEntry(filePathKey);
        metadataByKey.delete(filePathKey);
      },

      resetForTests() {
        lru.reset();
        inFlightByKey.clear();
        metadataByKey.clear();
        directURLByKey.clear();
        directURLUsedByKey.clear();
        urlRefreshedByKey.clear();
        set({entries: {}});
      },
    };
  });
}

/** Shared workspace blob cache; survives individual panel unmounts. */
export const useWorkspaceBlobStore = createWorkspaceBlobStore();
